import type {ContextProvider, ContextProviderDataGeneric} from '@bitstillery/common/lib/context'
import {to_specs} from '@bitstillery/common/lib/specs'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button} from '@bitstillery/common/components/ui/button/button'
import {Country} from '@bitstillery/common/components/ui/country/country'
import {FieldText} from '@bitstillery/common/components/fields/text/text'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {state, watch} from '@bitstillery/mithril'
import {debounce, present} from '@bitstillery/common/lib/utils'
import m from '@bitstillery/mithril'

import type {Unwatch} from '@bitstillery/mithril'
import type {CollectionProxy} from '@bitstillery/common/lib/collection'
import type {GetSellableItemCollectionViewResponse} from '@bitstillery/common/api/fact2server'

/** The sellable-items list's row, with the item tags its items_queried adds as text. */
type SellableRow = GetSellableItemCollectionViewResponse & {serialized_item_tags_as_str?: string}

/** A search suggestion: the sellable row it was made from, with the label and value FieldText shows. */
type SellableSuggestion = SellableRow & {label: string; value: string}

/** The selected product as the field shows it; every sales order item entity holds these fields. */
interface FieldProductEntity {
    alcohol_percentage?: string | number | null
    cases_per_pallet?: number | null
    cases_per_pallet_layer?: number | null
    country_of_origin?: string | null
    customs_status?: string | null
    gift_box_type?: string | null
    lot?: string | null
    number_of_bottles?: number | null
    number_of_cases_available?: number | null
    product_name?: string | null
    refill_status?: string | null
    tax_label?: string | null
    volume?: string | number | null
}

interface FieldProductAttrs {
    collection: CollectionProxy<SellableRow>
    context?: ContextProvider<ContextProviderDataGeneric & {countries_of_origin?: {country_of_origin: string | null}[]}>
    className?: string
    entity: FieldProductEntity
    entity_type: string
    linked?: boolean | number | string | null | undefined
    locked?: boolean | number | string | null
    filter: {selection: unknown}
    select_row: (row: SellableSuggestion) => unknown
    tabindex?: number
    debounce_on_search?: number
}

export class FieldProduct extends MithrilComponent<FieldProductAttrs> {
    // Do not directly edit filter.selection, that causes to many queries.
    data = state(
        {
            search: '',
        },
        'field-product-data',
    )

    watchers: Unwatch[] = []

    override oncreate(vnode: m.Vnode<FieldProductAttrs>) {
        const select_search = debounce(vnode.attrs.debounce_on_search || 0, () => {
            vnode.attrs.filter.selection = this.data.search
        })
        this.watchers.push(
            watch(this.data.$search, () => {
                void select_search()
            }),
        )
    }

    override onremove() {
        this.watchers.forEach((unwatch) => unwatch())
    }

    reset_selection(vnode: m.Vnode<FieldProductAttrs>) {
        vnode.attrs.filter.selection = ''
        present(vnode.attrs.context, 'product field context').reset_entity(vnode.attrs.entity_type)
    }

    view(vnode: m.Vnode<FieldProductAttrs>) {
        return (
            <div
                className='c-field-composed-product field'
                onkeydown={(e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        if (vnode.attrs.locked) {
                            this.reset_selection(vnode)
                        }
                    }
                }}
                tabindex={vnode.attrs.tabindex}
            >
                {!vnode.attrs.linked && (
                    <div className='search-container'>
                        <FieldText
                            autofocus={true}
                            className='js-field-focus'
                            help='Find a product with the desired specs'
                            label='Product name'
                            model={this.data.$search}
                            placeholder='Find a product...'
                            search={{
                                onsuggestion: (suggestion: SellableSuggestion) => {
                                    vnode.attrs.select_row(suggestion)
                                    this.data.search = ''
                                },
                                options: vnode.attrs.collection.state.items.map((i) => {
                                    const specs_line = to_specs({
                                        bottle_alcohol_percentage: i.alcohol_percentage,
                                        bottle_refill_status: i.refill_status,
                                        bottle_volume: i.volume,
                                        case_customs_status: i.customs_status,
                                        case_gift_box_type: i.gift_box_type || '',
                                        case_number_of_bottles: i.number_of_bottles,
                                        case_tax_label: i.tax_label,
                                    })
                                    // Append the tags to the drop down for better selection.
                                    let item_tags = i.serialized_item_tags_as_str || ''
                                    if (item_tags) {
                                        item_tags = `(${item_tags})`
                                    }

                                    return {
                                        label: `${specs_line} - ${i.product_name} ${item_tags}`,
                                        value: i.artkey,
                                        ...i,
                                    }
                                }),
                            }}
                            tabindex={vnode.attrs.tabindex}
                        />
                    </div>
                )}

                {vnode.attrs.linked && (
                    <div className='product-container'>
                        <div className='product'>
                            <label>Selected product:</label>
                            <div className='product-header'>
                                <div className='fl fl-g05'>
                                    {(() => {
                                        if (vnode.attrs.entity.country_of_origin) {
                                            return <Country country_code={vnode.attrs.entity.country_of_origin} />
                                        }
                                        const countries_of_origin = vnode.attrs.context?.data.countries_of_origin
                                        if (countries_of_origin) {
                                            return countries_of_origin.map((i) => <Country country_code={i.country_of_origin} />)
                                        }
                                        return null
                                    })()}
                                </div>
                                {vnode.attrs.entity.product_name}
                            </div>
                            <div className='product-specs'>
                                {to_specs({
                                    bottle_alcohol_percentage: vnode.attrs.entity.alcohol_percentage,
                                    bottle_refill_status: vnode.attrs.entity.refill_status,
                                    bottle_volume: vnode.attrs.entity.volume,
                                    case_customs_status: vnode.attrs.entity.customs_status,
                                    case_gift_box_type: vnode.attrs.entity.gift_box_type || '',
                                    case_number_of_bottles: vnode.attrs.entity.number_of_bottles,
                                    case_tax_label: vnode.attrs.entity.tax_label,
                                })}
                            </div>
                            {vnode.attrs.entity.lot && (
                                <div className='product-details'>
                                    <Icon name='stock' size='s' type='unset' />
                                    Available: {vnode.attrs.entity.number_of_cases_available} ({vnode.attrs.entity.lot})
                                </div>
                            )}
                            {Boolean(vnode.attrs.entity.cases_per_pallet) && (
                                <div className='product-details'>
                                    <Icon name='fork_lift' size='s' type='unset' /> Pallet: {vnode.attrs.entity.cases_per_pallet}{' '}
                                    cs / Layer: {vnode.attrs.entity.cases_per_pallet_layer} cs
                                </div>
                            )}
                        </div>

                        <Button
                            className='btn-close'
                            disabled={vnode.attrs.locked}
                            icon='close'
                            onclick={() => this.reset_selection(vnode)}
                            tabindex={vnode.attrs.tabindex}
                            type='info'
                            variant='toggle'
                        />
                    </div>
                )}

                <div className='help'>
                    {vnode.attrs.locked
                        ? 'Current product is locked'
                        : 'This is the product that will be added to the sales order.'}
                </div>
            </div>
        )
    }
}
